import { Input } from '@/components/atoms/Form/Input';
import { Switch } from '@/components/atoms/Switch';
import { computeText } from '@/locales/utils';
import { formatDate } from '@/utils/date';
import { useState } from 'react';

import {
    LoyaltyConfig,
    LoyaltyProgram,
    AuthenticationMethods,
    RegisterMethods,
    LoyaltyFeature,
} from '@/services/innovorder/modules/modules.type';
import React from 'react';
import { Control, Form } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { FormWrapper } from '../../SplioLoyaltyIntegration/SplioConfigUpdateForm/SplioConfigUpdateForm.style';
import { SwitchGroup } from '@/components/atoms/SwitchGroup';
import LoyaltyFeatures from '../../Loyalty/LoyaltyFeatures';

export interface UpdateComoConfigForm {
    formLink: string;
    useCasualTone: boolean;
    authenticationMethods: AuthenticationMethods;
    registerMethods: RegisterMethods;
    features: LoyaltyFeature[];
}

interface ComoConfigUpdateFormProps {
    control: Control<UpdateComoConfigForm>;
    loyaltyPrograms?: LoyaltyProgram[];
    loyaltyConfig?: LoyaltyConfig;
}

const ComoConfigUpdateForm: React.FC<ComoConfigUpdateFormProps> = ({
    control,
    loyaltyConfig,
}: ComoConfigUpdateFormProps) => {
    const intl = useIntl();

    return (
        <Form control={control}>
            <FormWrapper>
                <p>
                    {computeText(intl, 'como.integrationEnabled', {
                        date: loyaltyConfig?.createdAt
                            ? formatDate(loyaltyConfig.createdAt, 'DD/MM/YYYY HH:mm:ss')
                            : '',
                    })}
                </p>

                {loyaltyConfig && (
                    <Controller
                        name="formLink"
                        control={control}
                        rules={{
                            validate: (value) => {
                                try {
                                    if (!value) {
                                        return true;
                                    }
                                    const url = new URL(value);
                                    return (
                                        url.protocol === 'http:' ||
                                        url.protocol === 'https:' ||
                                        computeText(intl, 'como.error.formLinkInvalid')
                                    );
                                } catch (e) {
                                    return computeText(intl, 'como.error.formLinkInvalid');
                                }
                            },
                        }}
                        render={({ field: { onChange, value, ...otherProps }, formState: { errors } }) => (
                            <Input
                                labelId="como.formLink"
                                {...otherProps}
                                value={value}
                                onChange={onChange}
                                error={errors.formLink?.message}
                            />
                        )}
                        defaultValue={loyaltyConfig?.formLink}
                    />
                )}
                <Controller
                    name="useCasualTone"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                        <Switch labelId="como.useCasualTone" onChange={() => onChange(!value)} value={value} />
                    )}
                    defaultValue={loyaltyConfig?.useCasualTone}
                />

                <Controller
                    name="authenticationMethods"
                    control={control}
                    rules={{
                        validate: (value) => value.email || value.phone || value.externalId,
                    }}
                    render={({ field: { onChange, value } }) => (
                        <SwitchGroup
                            labelId="como.authenticationMethods"
                            value={value}
                            onChange={onChange}
                            options={[
                                { labelId: 'como.methods.email', name: 'email', value: value.email },
                                { labelId: 'como.methods.phone', name: 'phone', value: value.phone },
                                { labelId: 'como.methods.externalId', name: 'externalId', value: value.externalId },
                            ]}
                        />
                    )}
                    defaultValue={loyaltyConfig?.authenticationMethods}
                />

                <Controller
                    name="registerMethods"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                        <SwitchGroup
                            labelId="como.registerMethods"
                            value={value}
                            onChange={onChange}
                            options={[
                                { labelId: 'como.methods.email', name: 'email', value: value.email },
                                { labelId: 'como.methods.phone', name: 'phone', value: value.phone },
                            ]}
                            isUnique={true}
                        />
                    )}
                    defaultValue={loyaltyConfig?.registerMethods}
                />

                <Controller
                    name="features"
                    control={control}
                    defaultValue={loyaltyConfig?.features}
                    render={({ field: { onChange, value } }) => (
                        <LoyaltyFeatures features={value} onChange={onChange} />
                    )}
                />
            </FormWrapper>
        </Form>
    );
};

export default ComoConfigUpdateForm;
